import {
  TEditorConfig,
  IPdfGenerationPayload,
  IProductConfig,
  TProductMaterial,
  TProductShape,
  TDesignSide,
  IEditorWidget,
} from './editor.types'
import {
  ProductVariant,
  TOnlineEditorConfiguration,
} from '../designUpload/types'
import capitalize from 'lodash/capitalize'
import cloneDeep from 'lodash/cloneDeep'
import {
  ARTBOARD_SIZE,
  ARTBOARD_OFFSET_MD,
  INITIAL_BACKGROUND_COLOR,
  EMPTY_COLOR_STATE,
  INITIAL_ACCENT_COLOR,
  INITIAL_KRAFT_COLOR,
  initialLogoWidgetConfig,
  initialPatternWidgetConfig,
} from './editor.config'
import { ICanvasColors, TCanvas } from './Canvas'
import {
  IOnlineEditorColorOptionConfig,
  onlineEditorProductsColorsConfig,
} from './onlineEditorProductColors.config'
import camelCase from 'lodash/camelCase'
import isEmpty from 'lodash/isEmpty'
import { v4 as uuid } from 'uuid'
import { TColorOption } from '../briefingForm/briefingFormProductColors.config'
import { TFileWithState } from '../designUpload/components/UploadDropzone/UploadDropzone'
import { generatePDF } from '../../_utils'

export const roundToTwo = (num) => {
  return Math.round(num * 100) / 100
}

const mapProductSpecsToCanvas = (
  productSettings: TEditorConfig,
  artBoardSize: { width: number; height: number } = ARTBOARD_SIZE
) => {
  const {
    productBleedHeightInMm,
    productWidthInMm,
    productHeightInMm,
    productBleedWidthInMm,
    safeZoneOffsetsMm,
  } = productSettings

  const artBoardOffset = ARTBOARD_OFFSET_MD

  const availableHeight = artBoardSize.height - artBoardOffset * 2
  const availableWidth = artBoardSize.width - artBoardOffset * 2
  const productSketchHeight = productBleedHeightInMm * 2 + productHeightInMm
  const productSketchWidth = productBleedWidthInMm * 2 + productWidthInMm

  let canvasWidth = 0
  let canvasHeight = 0

  const sidesRatio = productSketchWidth / productSketchHeight

  if (productSketchWidth > productSketchHeight) {
    canvasWidth = availableWidth
    canvasHeight = roundToTwo(canvasWidth / sidesRatio)
  } else {
    canvasHeight = availableHeight
    canvasWidth = roundToTwo(canvasHeight * sidesRatio)
  }

  if (canvasWidth > availableWidth) {
    const widthScaleRatio = roundToTwo(availableWidth / canvasWidth)
    canvasWidth = availableWidth
    canvasHeight = canvasHeight * widthScaleRatio
  }
  if (canvasHeight > availableHeight) {
    const heightScaleRatio = roundToTwo(availableHeight / canvasHeight)
    canvasHeight = availableHeight
    canvasWidth = canvasWidth * heightScaleRatio
  }

  const conversionRatio = roundToTwo(productSketchWidth / canvasWidth)

  return {
    width: Math.max(canvasWidth, 0),
    height: Math.max(canvasHeight, 0),
    bleedHeight: roundToTwo(productBleedHeightInMm / conversionRatio),
    bleedWidth: roundToTwo(productBleedWidthInMm / conversionRatio),
    safeZoneOffsets: {
      top: safeZoneOffsetsMm?.top / conversionRatio,
      bottom: safeZoneOffsetsMm?.bottom / conversionRatio,
      left: safeZoneOffsetsMm?.left / conversionRatio,
      right: safeZoneOffsetsMm?.right / conversionRatio,
    },
    productSizeToCanvasRatio: conversionRatio,
    shape: productSettings?.shape,
    edgeType: productSettings?.edgeType,
    drillHoleLocation: productSettings?.drillHoleLocation,
  }
}

const constructPayload = ({
  canvasConfig,
  editorConfig,
}: {
  canvasConfig: Record<TDesignSide, TCanvas>
  editorConfig: TEditorConfig
}): IPdfGenerationPayload => {
  const sidesToPrint: TDesignSide[] = editorConfig?.twoSided
    ? ['front', 'back']
    : ['front']

  const usedColors = editorConfig?.productColorConfig
    ?.map((colorPicker) => {
      return sidesToPrint?.map((side) => {
        return {
          ...canvasConfig?.[side][camelCase(colorPicker.key)],
          colorType: colorPicker?.colorType,
        }
      })
    })
    .flat()

  return {
    productInfo: {
      name: editorConfig.productName,
      size: editorConfig.size,
      bleed: {
        width: editorConfig.productBleedWidthInMm,
        height: editorConfig.productBleedHeightInMm,
      },
      type: editorConfig.material,
    },
    artboard: {
      shape: canvasConfig.front.shape,
      sizeMm: {
        width:
          editorConfig.productBleedWidthInMm * 2 +
          editorConfig.productWidthInMm,
        height:
          editorConfig.productBleedHeightInMm * 2 +
          editorConfig.productHeightInMm,
      },
      // Removing duplicated colors with [..new Map(...)] trick
      colors: [
        ...new Map(usedColors.map((item) => [item['value'], item])).values(),
      ],
    },
    reorderId: '',
    svgWithDesign: {
      front: canvasConfig?.front?.canvasSvgData || null,
      back: canvasConfig?.back?.canvasSvgData || null,
    },
    pdfLayoutConfig: editorConfig?.pdfLayoutConfig,
    sidesToPrint: sidesToPrint,
  }
}

export const PRODUCT_OPTION_PROPS = {
  HEIGHT: 'productHeightInMm',
  WIDTH: 'productWidthInMm',
  LENGTH: 'productLengthInMm',
  SIDES: 'print option',
  BLEED_HEIGHT: 'productBleedHeightInMm',
  BLEED_WIDTH: 'productBleedWidthInMm',
  COLOR: 'colors',
  COLOR_ALTERNATIVE: 'color',
  SIZE: 'size',
  TYPE: 'type',
}

const getColorPickersConfig = (productSlug, colorOptionValue) => {
  let colorPickerConfig: IOnlineEditorColorOptionConfig[] = null

  if (colorOptionValue) {
    colorPickerConfig =
      onlineEditorProductsColorsConfig?.[productSlug]?.[colorOptionValue]
  }

  if (!colorPickerConfig) {
    colorPickerConfig = onlineEditorProductsColorsConfig?.[productSlug]?.default
  }

  return colorPickerConfig ? [...colorPickerConfig] : []
}

const checkForLegacyColorConfig = (preloadedConfig, productOption) => {
  if (isEmpty(preloadedConfig)) {
    return null
  }

  if (preloadedConfig?.productColorConfig?.pickers) {
    const newProductColorConfig = getColorPickersConfig(
      productOption.productSlug,
      productOption[PRODUCT_OPTION_PROPS.COLOR] ||
        productOption[PRODUCT_OPTION_PROPS.COLOR_ALTERNATIVE]
    )

    return {
      ...preloadedConfig,
      productColorConfig: newProductColorConfig,
    }
  }

  return preloadedConfig
}

const getProductMaterial = (
  productOption: ProductVariant,
  productName: string
) => {
  const selectedColorOption: TColorOption =
    productOption[PRODUCT_OPTION_PROPS.COLOR] ||
    productOption[PRODUCT_OPTION_PROPS.COLOR_ALTERNATIVE]

  if (
    productOption[PRODUCT_OPTION_PROPS.TYPE]?.toLowerCase() === 'kraft' ||
    (productName.toLowerCase().includes('kraft') &&
      selectedColorOption !== 'White')
  ) {
    return 'kraft'
  }
  return 'paper'
}

const mapProductOptionShapeToEditorConfig: Record<string, TProductShape> = {
  circle: 'circle',
  square: 'rectangle',
  rectangle: 'rectangle',
  oval: 'oval',
  beveled: 'beveled',
}

const generateEditorConfig = ({
  productEditorConfig,
  productOption,
}: {
  productOption: ProductVariant
  productEditorConfig: IProductConfig
}): TEditorConfig => {
  const productName = productOption.productSlug
    ?.split('-')
    ?.map((part) => capitalize(part))
    ?.join(' ')

  return {
    productBleedHeightInMm:
      typeof productEditorConfig?.bleedHeightOverride === 'number'
        ? productEditorConfig?.bleedHeightOverride
        : +productOption[PRODUCT_OPTION_PROPS.BLEED_HEIGHT] || 5,
    productBleedWidthInMm:
      typeof productEditorConfig?.bleedWidthOverride === 'number'
        ? productEditorConfig?.bleedWidthOverride
        : +productOption[PRODUCT_OPTION_PROPS.BLEED_WIDTH] || 5,
    /** Some products have length & width pair specified, instead of width & height pair
     * In that case product's length serves like canvas width and product's width serves as canvas height
     */
    productWidthInMm: +productOption[PRODUCT_OPTION_PROPS.LENGTH]
      ? +productOption[PRODUCT_OPTION_PROPS.LENGTH]
      : +productOption[PRODUCT_OPTION_PROPS.WIDTH],

    productHeightInMm: +productOption[PRODUCT_OPTION_PROPS.LENGTH]
      ? +productOption[PRODUCT_OPTION_PROPS.WIDTH]
      : +productOption[PRODUCT_OPTION_PROPS.HEIGHT],

    size: productOption[PRODUCT_OPTION_PROPS.SIZE],
    productColorConfig: getColorPickersConfig(
      productOption.productSlug,
      productOption[PRODUCT_OPTION_PROPS.COLOR] ||
        productOption[PRODUCT_OPTION_PROPS.COLOR_ALTERNATIVE]
    ),
    productColorOption:
      productOption[PRODUCT_OPTION_PROPS.COLOR] ||
      productOption[PRODUCT_OPTION_PROPS.COLOR_ALTERNATIVE],
    shape:
      mapProductOptionShapeToEditorConfig[
        productOption?.shape?.toLowerCase()
      ] || 'rectangle',
    edgeType: productOption?.['edge type']?.toLowerCase(),
    drillHoleLocation: productOption?.['drill hole location']?.toLowerCase(),
    enabledWidgets: productEditorConfig?.enabledWidgets,
    material: getProductMaterial(productOption, productName),
    productName: productName,
    productSlug: productOption.productSlug,
    pdfLayoutConfig: productEditorConfig?.pdfLayoutConfig,
    twoSided:
      Boolean(productOption[PRODUCT_OPTION_PROPS.SIDES] === '2 Sided') ||
      productEditorConfig?.isTwoSided,
    logoWidgetConfig: productEditorConfig?.logoWidgetConfig,
    patternWidgetConfig: productEditorConfig?.patternWidgetConfig,
    decorations: productEditorConfig?.decorations,
    sidesShareColors: productEditorConfig?.sidesShareColors,
    safeZoneOffsetsMm:
      productEditorConfig?.safeZoneOffsetPerSizeOverride?.[
        productOption[PRODUCT_OPTION_PROPS.SIZE]
      ] || productEditorConfig?.safeZoneOffsetsMm,
  }
}

function Loader() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center my-8 font-mori">
      <img
        className="w-32 h-32 rounded-full"
        src={'/icon-uploading-new-brand.gif'}
        alt="loading icon"
      />
      <span className="px-32 text-4xl font-semibold text-center pt-9 text-acai">
        We're processing your artwork... please hold
      </span>
    </div>
  )
}

function ErrorState() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center border-2 border-dashed text-error border-error rounded-[16px] font-mori">
      <span className="text-3xl font-semibold text-center px-14 pt-9">
        Oops! Something went wrong...
      </span>
      <span className="text-2xl text-center px-14 pt-9">
        We're having trouble processing that file. Try using a different file,
        or switching design path.
      </span>
    </div>
  )
}

const getOnlineEditorCustomizationObject = async ({
  onlineEditorOrderPayload,
  pdfGenerationPayload,
  parentOrderDesignId,
}: {
  onlineEditorOrderPayload: TOnlineEditorConfiguration
  pdfGenerationPayload: IPdfGenerationPayload
  parentOrderDesignId: string
}) => {
  const { pdfUrl, designId: bucketDesignId } = await generatePDF(
    pdfGenerationPayload,
    parentOrderDesignId
  )
  const customizationPayload = cloneDeep(onlineEditorOrderPayload)

  for (let side of Object.keys(customizationPayload.canvasConfig)) {
    delete customizationPayload?.canvasConfig?.[side]?.canvasSvgData

    customizationPayload.canvasConfig[side].widgets =
      customizationPayload?.canvasConfig?.[side]?.widgets.map((widget) => {
        // Removing to prevent sending large customization payloads
        // Image data URLs aren't needed for pre press team and will be recreated by editor in case of reoder
        delete widget?.config?.assetSvgData
        delete widget?.config?.assetUrl
        return widget
      })
  }

  customizationPayload.editorConfig.productColorConfig =
    customizationPayload?.editorConfig?.productColorConfig?.map((picker) => ({
      key: picker.key,
    })) as IOnlineEditorColorOptionConfig[]

  return {
    ...customizationPayload,
    pdfUrl,
    bucketDesignId,
  }
}

const getResetCanvasColors = ({
  enabledPickers = [],
  material = 'paper',
  productColorOption,
}: {
  enabledPickers: string[]
  material: TProductMaterial
  productColorOption: TColorOption
}): ICanvasColors => {
  let backgroundColor = INITIAL_BACKGROUND_COLOR

  if (
    (enabledPickers.includes('background-color') &&
      enabledPickers?.length === 1) ||
    productColorOption === 'Black'
  ) {
    backgroundColor = INITIAL_ACCENT_COLOR
  }

  if (material === 'kraft') {
    backgroundColor = INITIAL_KRAFT_COLOR
  }

  return {
    backgroundColor: backgroundColor,
    logoColor:
      enabledPickers.includes('logo-color') && productColorOption !== 'Black'
        ? INITIAL_ACCENT_COLOR
        : EMPTY_COLOR_STATE,
  }
}

function getColorPickerKeys(
  productColorConfig: IOnlineEditorColorOptionConfig[] = []
) {
  return productColorConfig?.map((picker) => picker.key) || []
}

function getInitialWidgetConfig({
  editorConfig,
}: {
  editorConfig: TEditorConfig
}): IEditorWidget {
  return editorConfig?.enabledWidgets?.logo
    ? {
        type: 'logo',
        id: uuid(),
        config: {
          ...initialLogoWidgetConfig,
          ...editorConfig?.logoWidgetConfig,
        },
      }
    : {
        type: 'pattern',
        id: uuid(),
        config: {
          ...initialPatternWidgetConfig(),
          ...editorConfig?.patternWidgetConfig,
        },
      }
}

const findFileByName = (filesWithState, fileName) => {
  if (!filesWithState || !fileName) return null

  return (
    filesWithState?.find(
      (fileWithState) =>
        fileWithState?.file?.name === fileName.trim().replaceAll(' ', '')
    ) || null
  )
}

const loadImage = async (imgSrc: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = imgSrc
  })
}

const getArtworkDPI = ({
  productWidthInInches,
  file,
}: {
  productWidthInInches: number
  file: TFileWithState
}) => {
  return file?.metaData?.width / productWidthInInches || 0
}

export {
  getOnlineEditorCustomizationObject,
  mapProductSpecsToCanvas,
  constructPayload,
  generateEditorConfig,
  Loader,
  ErrorState,
  getResetCanvasColors,
  getColorPickerKeys,
  checkForLegacyColorConfig,
  getInitialWidgetConfig,
  findFileByName,
  loadImage,
  getArtworkDPI,
}
